"use client";
import React from 'react';
import { motion } from 'motion/react';
import { Spotlight } from '@/components/ui/spotlight-new';

export type AuthBackgroundConfig = {
    // Spotlight configuration
    spotlightIntensity?: 'subtle' | 'normal' | 'intense';
    focusPosition?: 'center' | 'left' | 'right' | 'form-focus';
    animationSpeed?: 'slow' | 'normal' | 'fast';
    colorScheme?: 'purple-blue' | 'cyan-green' | 'warm' | 'cool';

    // Form-specific targeting
    formSelector?: string;
    enableFormFocus?: boolean;

    // Additional effects
    enableAccentGradients?: boolean;
    enableTopFade?: boolean;
    enableBottomFade?: boolean;
};

const colorSchemes = {
    'purple-blue': {
        gradientFirst: "radial-gradient(68.54% 68.72% at 55.02% 31.46%, hsla(260, 100%, 85%, .12) 0, hsla(240, 100%, 70%, .08) 30%, hsla(220, 100%, 60%, .04) 60%, hsla(200, 100%, 45%, 0) 80%)",
        gradientSecond: "radial-gradient(50% 50% at 50% 50%, hsla(280, 100%, 90%, .10) 0, hsla(260, 100%, 75%, .06) 40%, hsla(240, 100%, 55%, .03) 80%, transparent 100%)",
        gradientThird: "radial-gradient(50% 50% at 50% 50%, hsla(300, 100%, 85%, .08) 0, hsla(280, 100%, 65%, .04) 50%, hsla(260, 100%, 45%, .02) 80%, transparent 100%)",
        accent1: "hsla(260,100%,85%,.03)",
        accent2: "hsla(200,100%,85%,.02)"
    },
    'cyan-green': {
        gradientFirst: "radial-gradient(68.54% 68.72% at 55.02% 31.46%, hsla(180, 100%, 85%, .12) 0, hsla(160, 100%, 70%, .08) 30%, hsla(140, 100%, 60%, .04) 60%, hsla(120, 100%, 45%, 0) 80%)",
        gradientSecond: "radial-gradient(50% 50% at 50% 50%, hsla(200, 100%, 90%, .10) 0, hsla(180, 100%, 75%, .06) 40%, hsla(160, 100%, 55%, .03) 80%, transparent 100%)",
        gradientThird: "radial-gradient(50% 50% at 50% 50%, hsla(220, 100%, 85%, .08) 0, hsla(200, 100%, 65%, .04) 50%, hsla(180, 100%, 45%, .02) 80%, transparent 100%)",
        accent1: "hsla(180,100%,85%,.03)",
        accent2: "hsla(140,100%,85%,.02)"
    },
    'warm': {
        gradientFirst: "radial-gradient(68.54% 68.72% at 55.02% 31.46%, hsla(30, 100%, 85%, .12) 0, hsla(20, 100%, 70%, .08) 30%, hsla(10, 100%, 60%, .04) 60%, hsla(0, 100%, 45%, 0) 80%)",
        gradientSecond: "radial-gradient(50% 50% at 50% 50%, hsla(40, 100%, 90%, .10) 0, hsla(30, 100%, 75%, .06) 40%, hsla(20, 100%, 55%, .03) 80%, transparent 100%)",
        gradientThird: "radial-gradient(50% 50% at 50% 50%, hsla(50, 100%, 85%, .08) 0, hsla(40, 100%, 65%, .04) 50%, hsla(30, 100%, 45%, .02) 80%, transparent 100%)",
        accent1: "hsla(30,100%,85%,.03)",
        accent2: "hsla(10,100%,85%,.02)"
    },
    'cool': {
        gradientFirst: "radial-gradient(68.54% 68.72% at 55.02% 31.46%, hsla(220, 100%, 85%, .12) 0, hsla(210, 100%, 70%, .08) 30%, hsla(200, 100%, 60%, .04) 60%, hsla(190, 100%, 45%, 0) 80%)",
        gradientSecond: "radial-gradient(50% 50% at 50% 50%, hsla(230, 100%, 90%, .10) 0, hsla(220, 100%, 75%, .06) 40%, hsla(210, 100%, 55%, .03) 80%, transparent 100%)",
        gradientThird: "radial-gradient(50% 50% at 50% 50%, hsla(240, 100%, 85%, .08) 0, hsla(230, 100%, 65%, .04) 50%, hsla(220, 100%, 45%, .02) 80%, transparent 100%)",
        accent1: "hsla(220,100%,85%,.03)",
        accent2: "hsla(190,100%,85%,.02)"
    }
};

const intensityMultipliers = {
    'subtle': 0.6,
    'normal': 1,
    'intense': 1.4
};

const animationSpeeds = {
    'slow': 20,
    'normal': 14,
    'fast': 8
};

const focusPositions = {
    'center': { translateY: -300, xOffset: 0 },
    'left': { translateY: -250, xOffset: -80 },
    'right': { translateY: -250, xOffset: 80 },
    'form-focus': { translateY: -150, xOffset: 0 }
};

export function AuthBackground({
    spotlightIntensity = 'normal',
    focusPosition = 'form-focus',
    animationSpeed = 'normal',
    colorScheme = 'purple-blue',
    enableFormFocus = true,
    enableAccentGradients = true,
    enableTopFade = true,
    enableBottomFade = true
}: AuthBackgroundConfig = {}) {

    const colors = colorSchemes[colorScheme];
    const intensity = intensityMultipliers[spotlightIntensity];
    const duration = animationSpeeds[animationSpeed];
    const position = focusPositions[focusPosition];

    // Adjust gradient opacity based on intensity
    const adjustGradientIntensity = (gradient: string) => {
        return gradient.replace(/hsla\(([^)]+)\)/g, (match, params) => {
            const parts = params.split(',');
            if (parts.length >= 4) {
                const alpha = parseFloat(parts[3].trim());
                const newAlpha = Math.min(1, alpha * intensity);
                parts[3] = ` ${newAlpha}`;
                return `hsla(${parts.join(',')})`;
            }
            return match;
        });
    };

    return (
        <>
            {/* Main container with overflow hidden */}
            <div className="fixed inset-0 -z-50 overflow-hidden">
                {/* Configurable Spotlight Effects */}
                <div className="absolute inset-0 overflow-hidden">
                    <Spotlight
                        gradientFirst={adjustGradientIntensity(colors.gradientFirst)}
                        gradientSecond={adjustGradientIntensity(colors.gradientSecond)}
                        gradientThird={adjustGradientIntensity(colors.gradientThird)}
                        translateY={position.translateY}
                        width={enableFormFocus ? 350 : 400}
                        height={enableFormFocus ? 800 : 1200}
                        smallWidth={enableFormFocus ? 150 : 180}
                        duration={duration}
                        xOffset={position.xOffset}
                    />
                </div>

                {/* Form-focused spotlight overlay - Points directly at form */}
                {enableFormFocus && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.5, delay: 0.5 }}
                        className="absolute inset-0 -z-45 overflow-hidden"
                    >
                        {/* Primary form spotlight - positioned to highlight the form card */}
                        <div
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[600px]"
                            style={{
                                background: `radial-gradient(ellipse 250px 300px at center, ${colors.accent1} 0%, ${colors.accent2} 30%, transparent 70%)`,
                                filter: 'blur(40px)',
                                transform: 'translate(-50%, -50%) scale(1.1)'
                            }}
                        />

                        {/* Secondary focused beam - creates directional pointing effect */}
                        <div
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[400px]"
                            style={{
                                background: `radial-gradient(ellipse 150px 200px at center, ${colors.accent1} 0%, transparent 60%)`,
                                filter: 'blur(25px)',
                                transform: 'translate(-50%, -50%)'
                            }}
                        />

                        {/* Precise form targeting spotlight - Static for better performance */}
                        <div
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[500px] opacity-80"
                            style={{
                                background: `radial-gradient(ellipse 200px 250px at center, ${colors.accent1} 0%, transparent 60%)`,
                                filter: 'blur(15px)',
                                transform: 'translate(-50%, -50%)'
                            }}
                        />
                    </motion.div>
                )}

                {/* Radial Gradient Overlay - Bottom fade */}
                {enableBottomFade && (
                    <div className="absolute inset-0 -z-10 size-full [background:radial-gradient(125%_125%_at_50%_100%,transparent_0%,var(--color-background)_75%)]"></div>
                )}

                {/* Additional subtle gradient for auth pages - Top fade */}
                {enableTopFade && (
                    <div className="absolute inset-0 -z-5 size-full [background:radial-gradient(100%_100%_at_50%_0%,transparent_0%,var(--color-background)_60%)]"></div>
                )}

                {/* Static subtle accent gradients - removed animations for performance */}
                {enableAccentGradients && (
                    <>
                        <div
                            className="absolute inset-0 -z-8 size-full opacity-60"
                            style={{
                                background: `radial-gradient(60%_60%_at_20%_20%,${colors.accent1}_0%,transparent_40%)`
                            }}
                        />

                        <div
                            className="absolute inset-0 -z-7 size-full opacity-40"
                            style={{
                                background: `radial-gradient(50%_50%_at_80%_80%,${colors.accent2}_0%,transparent_40%)`
                            }}
                        />
                    </>
                )}
            </div>
        </>
    );
}
